// src/components/admin/DeviceManagement.tsx
import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Devices as DevicesIcon,
  QrCodeScanner as QrCodeIcon,
  Info as InfoIcon,
  Lock as LockIcon,
  Computer as ComputerIcon,
  Visibility as ViewIcon,
  MoreVert as MoreIcon
} from '@mui/icons-material';
import { getUser } from '../common/utils';
import { IonPage, IonContent } from '@ionic/react';
import { deviceApi } from '../common/deviceApi';
import { MenuButton } from '../components/Menu';

interface Device {
  id: number;
  user_id: number;
  device_name: string;
  device_hash: string;
  is_active: boolean;
  registered_at: string;
  last_used_at: string | null;
  user_email?: string;
  user_name?: string;
  browser_info?: string;
  ip_address?: string;
}

const DeviceManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const userId = getUser().loginData.user_id;
  const [devices, setDevices] = useState<Device[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [registerLoading, setRegisterLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [newDeviceCode, setNewDeviceCode] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deviceDetailsDialogOpen, setDeviceDetailsDialogOpen] = useState<boolean>(false);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });

  // Fetch user's devices
  const fetchDevices = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await deviceApi.getAllDevices();
      console.log('Fetched devices result:', result);
      
      if (result.status === true) {
        const devicesData = result.data?.devices || [];
        console.log('Devices data:', devicesData);
        
        const transformedDevices: Device[] = devicesData.map((device: any) => ({
          id: device.id,
          user_id: device.user_id,
          device_name: device.device_name,
          device_hash: device.device_hash,
          is_active: device.is_active === 1,
          registered_at: device.registered_at,
          last_used_at: device.last_used_at,
          user_email: device.user_email,
          user_name: device.user_name,
          browser_info: device.browser_info,
          ip_address: device.ip_address
        }));
        
        console.log('Transformed devices:', transformedDevices);
        setDevices(transformedDevices);
        
        const activeCount = transformedDevices.filter(d => d.is_active).length;
        setStats({
          total: transformedDevices.length,
          active: activeCount,
          inactive: transformedDevices.length - activeCount
        });
        
      } else {
        setError('Failed to fetch devices: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error fetching devices:', error);
      setError('Error fetching devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  // Register new device
  const handleRegisterDevice = async () => {
    if (!newDeviceCode.trim() || !userId) {
      setError('Please enter a device code');
      return;
    }
    
    setRegisterLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const result = await deviceApi.registerDevice(
        userId, 
        newDeviceCode.trim(),
        'Manually Registered Device'
      );
      
      console.log('Register device result:', result);
      
      if (result.status === true) {
        setNewDeviceCode('');
        setSuccess('Device registered successfully!');
        fetchDevices();
      } else {
        setError('Error: ' + (result.message || 'Failed to register device'));
      }
    } catch (error) {
      console.error('Error registering device:', error);
      setError('Error registering device. Please try again.');
    } finally {
      setRegisterLoading(false);
    }
  };

  // Handle device deactivation
  const handleDeactivateDevice = async () => {
    if (!selectedDevice) return;
    
    console.log('Deactivating device:', selectedDevice);
    try {
      const result = await deviceApi.deactivateDevice(selectedDevice.id, selectedDevice.user_id);
      console.log('Deactivate device result:', result);
      
      if (result.status === true) {
        setSuccess(`Device "${selectedDevice.device_name}" has been deactivated`);
        fetchDevices();
      } else {
        setError('Failed to deactivate device: ' + (result.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deactivating device:', error);
      setError('Error deactivating device. Please try again.');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedDevice(null);
    }
  };

  // Copy device hash to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSuccess('Device hash copied to clipboard!');
      setTimeout(() => setSuccess(''), 3000);
    }).catch(() => {
      setError('Failed to copy to clipboard');
    });
  };

  // View device details
  const handleViewDeviceDetails = (device: Device) => {
    setSelectedDevice(device);
    setDeviceDetailsDialogOpen(true);
  };

  // Filter devices based on search
  const filteredDevices = devices.filter(device => 
    device.device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.device_hash?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.browser_info?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.ip_address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calculate paginated data
  const paginatedDevices = filteredDevices.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return isMobile 
      ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
  };

  // Format browser info for display
  const formatBrowserInfo = (browserInfo: string) => {
    if (!browserInfo) return 'N/A';
    if (browserInfo.includes('Chrome')) return 'Chrome';
    if (browserInfo.includes('Firefox')) return 'Firefox';
    if (browserInfo.includes('Safari')) return 'Safari';
    if (browserInfo.includes('Edge')) return 'Edge';
    if (browserInfo.includes('Opera')) return 'Opera';
    return 'Browser';
  };

  // Truncate text for mobile view
  const truncateText = (text: string, maxLength: number = 10) => {
    if (isMobile && text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text;
  };

  return (
    <IonPage className='pushDown'>
      <MenuButton />
      <IonContent scrollY={true}>
        <Box sx={{ 
          p: { xs: 2, sm: 3 }, 
          bgcolor: 'background.default', 
          minHeight: '100%',
          maxWidth: '100%',
          overflowX: 'hidden'
        }}>
          {/* Header */}
          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <DevicesIcon sx={{ 
                fontSize: { xs: 24, sm: 32 }, 
                color: 'primary.main', 
                mr: 2 
              }} />
              <Typography variant={isMobile ? "h5" : "h4"} component="h1" sx={{ fontWeight: 'bold' }}>
                Device Management
              </Typography>
            </Box>
            <Typography variant={isMobile ? "body2" : "body1"} color="text.secondary">
              Manage and monitor registered devices for user authentication
            </Typography>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={isMobile ? 1 : 2} sx={{ mb: { xs: 3, sm: 4 } }}>
            {[
              { value: stats.total, label: 'Total Devices', color: 'primary.main' },
              { value: stats.active, label: 'Active Devices', color: 'success.main' },
              { value: stats.inactive, label: 'Inactive Devices', color: stats.inactive > 0 ? 'warning.main' : 'grey.700' }
            ].map((stat, index) => (
              <Grid item xs={4} sm={4} md={4} key={index}>
                <Card 
                  sx={{ 
                    bgcolor: stat.color, 
                    color: 'common.white',
                    borderRadius: 2,
                    height: '100%',
                    minHeight: { xs: 80, sm: 100 }
                  }}
                >
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant={isMobile ? "h4" : "h2"} sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant={isMobile ? "caption" : "body2"} sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Register New Device Card */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: { xs: 3, sm: 4 }, 
              bgcolor: alpha('#4caf50', 0.05),
              border: '1px solid',
              borderColor: 'success.light',
              borderRadius: 2,
              width: '100%',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AddIcon sx={{ color: 'success.main', mr: 1, fontSize: { xs: 18, sm: 24 } }} />
              <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 'bold' }}>
                Register New Device
              </Typography>
            </Box>
            
            <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary" sx={{ mb: 2 }}>
              Enter the device code provided by the user to register their device
            </Typography>
            
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center', 
              flexDirection: { xs: 'column', sm: 'row' },
              width: '100%'
            }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Paste device code here..."
                value={newDeviceCode}
                onChange={(e) => setNewDeviceCode(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeIcon sx={{ color: 'text.secondary', fontSize: { xs: 18, sm: 24 } }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    flex: 1,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }
                }}
                sx={{ flex: 1, width: '100%' }}
              />
              <Button
                variant="contained"
                color="success"
                onClick={handleRegisterDevice}
                disabled={!newDeviceCode.trim() || registerLoading}
                startIcon={registerLoading ? <CircularProgress size={20} /> : <CheckIcon />}
                sx={{ 
                  minWidth: { xs: '100%', sm: 120 },
                  height: { xs: 40, sm: 40 }
                }}
                fullWidth={isMobile}
              >
                {registerLoading ? 'Registering...' : 'Register'}
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}>
              <InfoIcon sx={{ 
                fontSize: { xs: 14, sm: 16 }, 
                color: 'text.secondary', 
                mr: 1,
                mt: 0.25 
              }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Device codes are typically provided by users during login when device verification is required
              </Typography>
            </Box>
          </Paper>

          {/* Messages */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}

          {/* Devices Table Section */}
          <Paper 
            elevation={0}
            sx={{ 
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              width: '100%',
              maxWidth: '100%',
              overflowX: 'auto'
            }}
          >
            <Box sx={{ 
              p: { xs: 2, sm: 3 }, 
              borderBottom: '1px solid', 
              borderColor: 'divider',
              minWidth: { xs: 600, sm: 'auto' }
            }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                width: '100%'
              }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                  Registered Devices ({devices.length})
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1, sm: 2 }, 
                  alignItems: 'center', 
                  width: { xs: '100%', sm: 'auto' } 
                }}>
                  <TextField
                    size="small"
                    placeholder="Search devices..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      sx: { 
                        borderRadius: 2,
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                        height: { xs: 36, sm: 40 }
                      }
                    }}
                    sx={{ 
                      flex: 1,
                      minWidth: { xs: 'auto', sm: 200 }
                    }}
                  />
                  
                  <Tooltip title="Refresh devices">
                    <IconButton 
                      onClick={fetchDevices} 
                      disabled={loading}
                      sx={{ borderRadius: 2 }}
                      size={isMobile ? "small" : "medium"}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center', minWidth: 600 }}>
                <CircularProgress size={isMobile ? 30 : 40} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Loading devices...
                </Typography>
              </Box>
            ) : filteredDevices.length === 0 ? (
              <Box sx={{ 
                p: { xs: 4, sm: 8 }, 
                textAlign: 'center',
                minWidth: 600 
              }}>
                <LockIcon sx={{ 
                  fontSize: { xs: 48, sm: 64 }, 
                  color: 'text.disabled', 
                  mb: 2 
                }} />
                <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary" gutterBottom>
                  {searchTerm ? 'No devices match your search' : 'No devices found'}
                </Typography>
                <Typography variant={isMobile ? "caption" : "body2"} color="text.secondary">
                  {searchTerm ? 'Try a different search term' : 'Register a device to get started'}
                </Typography>
              </Box>
            ) : (
              <>
                {isMobile ? (
                  // Mobile view - Card list
                  <Box sx={{ p: 2 }}>
                    {paginatedDevices.map((device) => (
                      <Paper
                        key={device.id}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          bgcolor: device.is_active ? 'background.paper' : 'action.hover'
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <ComputerIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                            <Typography variant="subtitle2" fontWeight="medium" sx={{ flex: 1 }}>
                              {truncateText(device.device_name, 20)}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            label={device.is_active ? 'Active' : 'Inactive'}
                            color={device.is_active ? 'success' : 'error'}
                            sx={{ height: 20, fontSize: '0.6rem' }}
                          />
                        </Box>
                        
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Hash: {truncateText(device.device_hash, 12)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Browser: {formatBrowserInfo(device.browser_info || '')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            IP: {truncateText(device.ip_address || 'N/A', 15)}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(device.registered_at)}
                          </Typography>
                          <Box>
                            <Tooltip title="View details">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDeviceDetails(device)}
                                sx={{ mr: 0.5 }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {device.is_active && (
                              <Tooltip title="Deactivate">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setSelectedDevice(device);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  // Desktop/Tablet view - Table
                  <TableContainer sx={{ minWidth: 600 }}>
                    <Table sx={{ minWidth: 600 }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Device</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Device Hash</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Browser</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>IP Address</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Registered</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', py: 2 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedDevices.map((device) => (
                          <TableRow 
                            key={device.id}
                            hover
                            sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                          >
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ComputerIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                <Box>
                                  <Typography variant="body2" fontWeight="medium">
                                    {device.device_name}
                                  </Typography>
                                  {device.user_name && (
                                    <Typography variant="caption" color="text.secondary">
                                      {device.user_name}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                                  {device.device_hash.substring(0, 16)}...
                                </Typography>
                                <Tooltip title="Copy hash">
                                  <IconButton 
                                    size="small"
                                    onClick={() => copyToClipboard(device.device_hash)}
                                  >
                                    <CopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Tooltip title={device.browser_info || 'Unknown browser'}>
                                <Chip
                                  size="small"
                                  label={formatBrowserInfo(device.browser_info || '')}
                                  variant="outlined"
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {device.ip_address || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2">
                                {formatDate(device.registered_at)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Last used: {formatDate(device.last_used_at)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.5 }}>
                              <Chip
                                size="small"
                                icon={device.is_active ? <CheckIcon /> : <CancelIcon />}
                                label={device.is_active ? 'Active' : 'Inactive'}
                                color={device.is_active ? 'success' : 'error'}
                                variant="outlined"
                                sx={{ minWidth: 80 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                <Tooltip title="View details">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewDeviceDetails(device)}
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {device.is_active && (
                                  <Tooltip title="Deactivate device">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => {
                                        setSelectedDevice(device);
                                        setDeleteDialogOpen(true);
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <TablePagination
                  rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
                  component="div"
                  count={filteredDevices.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  sx={{ 
                    borderTop: '1px solid', 
                    borderColor: 'divider',
                    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }
                  }}
                />
              </>
            )}
          </Paper>

          {/* Device Details Dialog */}
          <Dialog
            open={deviceDetailsDialogOpen}
            onClose={() => setDeviceDetailsDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            scroll="paper"
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ComputerIcon />
                Device Details
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              {selectedDevice && (
                <Box sx={{ mt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Device Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium', mb: 1 }}>
                        {selectedDevice.device_name}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">User</Typography>
                      <Typography variant="body1">
                        {selectedDevice.user_name || 'N/A'} ({selectedDevice.user_email || 'N/A'})
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                      <Chip
                        size="small"
                        label={selectedDevice.is_active ? 'Active' : 'Inactive'}
                        color={selectedDevice.is_active ? 'success' : 'error'}
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Device Hash</Typography>
                      <Box sx={{ 
                        p: 1, 
                        bgcolor: 'grey.100', 
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        wordBreak: 'break-all'
                      }}>
                        {selectedDevice.device_hash}
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Browser</Typography>
                      <Typography variant="body1">
                        {selectedDevice.browser_info || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                      <Typography variant="body1" fontFamily="monospace">
                        {selectedDevice.ip_address || 'N/A'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Registered</Typography>
                      <Typography variant="body1">
                        {formatDate(selectedDevice.registered_at)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Last Used</Typography>
                      <Typography variant="body1">
                        {formatDate(selectedDevice.last_used_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeviceDetailsDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon color="error" />
                Deactivate Device
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to deactivate this device?
              </Typography>
              {selectedDevice && (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ComputerIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {selectedDevice.device_name}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Hash: {selectedDevice.device_hash.substring(0, 20)}...
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Browser: {formatBrowserInfo(selectedDevice.browser_info || '')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div">
                    IP: {selectedDevice.ip_address || 'N/A'}
                  </Typography>
                </Paper>
              )}
              <Alert severity="warning" sx={{ fontSize: '0.875rem' }}>
                The user will need to re-register this device to use it again.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={handleDeactivateDevice}
                startIcon={<DeleteIcon />}
              >
                Deactivate
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </IonContent>
    </IonPage>
  );
};

export default DeviceManagement;